import type { PhysicalProperties } from './physics/materialPhysicalProperties'
import {
  AVATAR_ID,
  EditablePreviewWorld,
  IDENTITY_QUAT,
  NewObjectSpec,
  ObjectRuntimeState,
  ObjectVelocityRequest,
  Quat,
  RobloxMaterial,
  SceneDescriptor,
  ShapeKind,
  Vec3,
  WorldChangeListener,
} from './types'

/** アバター色が未指定のときの既定色。 */
const DEFAULT_AVATAR_COLOR = '#2e86de'

/** マテリアル未指定時の既定値。 */
const DEFAULT_MATERIAL: RobloxMaterial = 'Plastic'

/** アバターのジャンプ力既定（Roblox Humanoid.JumpPower 既定）。 */
const DEFAULT_JUMP_POWER = 50
/** アバターの体力既定（Roblox Humanoid.MaxHealth 既定）。 */
const DEFAULT_MAX_HEALTH = 100

/** ビルダーで作れる最小寸法（0/負を避ける）。 */
const MIN_SIZE = 0.05

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/**
 * `initialState` の入力。位置引数だと canCollide / shape / 回転など rbxl 由来
 * フィールドを追加するたび順序バグを招くため、オプションオブジェクトで受ける。
 */
interface InitialStateInput {
  id: string
  name: string
  position: Vec3
  color: string
  size: Vec3
  material: RobloxMaterial
  transparency?: number
  rotationY?: number
  /** rbxl 由来の基準向き（X/Z 傾き含む）。未指定時は単位（無回転）。 */
  orientation?: Quat
  /** rbxl 由来 CanCollide。未指定時のみ「完全透明=非衝突」の従来推定にフォールバック。 */
  canCollide?: boolean
  /** rbxl 由来 CanTouch。未指定時は true（Roblox 既定）。 */
  canTouch?: boolean
  /** rbxl 由来 Anchored（descriptor.isStatic）。未指定時は固定（true）扱い。 */
  isStatic?: boolean
  /**
   * 非 Anchored でも「うごくようにする」で解除されるまで固定扱いにする
   * （descriptor.dynamicOnUnanchor）。初期 anchored を true にする。
   */
  dynamicOnUnanchor?: boolean
  /** 描画形状。未指定時は box。 */
  shape?: ShapeKind
  /** 炎エフェクト（Roblox Fire 相当）。未指定時は false。 */
  fire?: boolean
  /** キラキラエフェクト（Roblox Sparkles 相当）。未指定時は false。 */
  sparkle?: boolean
  /** 動的剛体として生成するときの初速（studs/秒）。未指定時は無し。 */
  initialVelocity?: Vec3
  /** 効果音の初期音量（Roblox Sound.Volume、0〜10）。未指定は Sound なし。 */
  soundVolume?: number
  physicalProperties?: PhysicalProperties
  /** 当たり判定サイズの倍率（未指定時 1）。1 未満でコライダーを見た目より小さく。 */
  collisionScale?: number
}

const initialState = (input: InitialStateInput): ObjectRuntimeState => {
  const transparency = clamp(input.transparency ?? 0, 0, 1)
  return {
    id: input.id,
    name: input.name,
    position: { ...input.position },
    rotationY:
      Number.isFinite(input.rotationY) ? (input.rotationY as number) : 0,
    orientation: input.orientation ?? IDENTITY_QUAT,
    transparency,
    visible: true,
    color: input.color,
    size: { ...input.size },
    material: input.material,
    shape: input.shape ?? 'box',
    fire: input.fire ?? false,
    sparkle: input.sparkle ?? false,
    // CanCollide は rbxl 由来値を最優先。未指定時のみ、完全透明（半透明UI部品）は
    // コリジョン無効が自然という従来推定を使う。
    canCollide: input.canCollide ?? transparency < 1,
    canTouch: input.canTouch ?? true,
    clickable: false,
    // 既定は固定（true）。明示的に isStatic=false の部品だけ動的になり得る。
    // dynamicOnUnanchor の部品は非 Anchored でも「うごくようにする」で解除される
    // まで固定扱い（例: RJF12 の ①ボール）。
    anchored: input.isStatic !== false || input.dynamicOnUnanchor === true,
    initialVelocity:
      input.initialVelocity ? { ...input.initialVelocity } : undefined,
    // Roblox Sound.Volume の範囲 0〜10 に防御的にクランプ（setSoundVolume と同じ）。
    soundVolume:
      input.soundVolume !== undefined ?
        clamp(input.soundVolume, 0, 10)
      : undefined,
    collisionScale: input.collisionScale ?? 1,
    physicalProperties:
      input.physicalProperties ? { ...input.physicalProperties } : undefined,
  }
}

const isFiniteVec3 = (v: Vec3): boolean =>
  Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z)

const isPositiveVec3 = (v: Vec3): boolean => v.x > 0 && v.y > 0 && v.z > 0

/** 各軸を有限かつ最小寸法以上に正規化する（負は絶対値）。 */
const normalizeSize = (v: Vec3): Vec3 => ({
  x: Number.isFinite(v.x) ? Math.max(MIN_SIZE, Math.abs(v.x)) : MIN_SIZE,
  y: Number.isFinite(v.y) ? Math.max(MIN_SIZE, Math.abs(v.y)) : MIN_SIZE,
  z: Number.isFinite(v.z) ? Math.max(MIN_SIZE, Math.abs(v.z)) : MIN_SIZE,
})

/** 非有限値（NaN/Infinity）を World 境界で弾く。 */
const assertFinite = (value: number, label: string): void => {
  if (!Number.isFinite(value)) {
    throw new Error(`${label} は有限値である必要があります（受領: ${value}）`)
  }
}

const assertFiniteVec3 = (v: Vec3, label: string): void => {
  assertFinite(v.x, `${label}.x`)
  assertFinite(v.y, `${label}.y`)
  assertFinite(v.z, `${label}.z`)
}

/**
 * シーン記述の不正を起動前に検出して throw する。
 * 重複 id/name、予約 ID(AVATAR_ID) の誤用、非有限座標、非正サイズを弾く。
 */
const validateSceneDescriptor = (descriptor: SceneDescriptor): void => {
  const ids = new Set<string>()
  const names = new Set<string>()
  for (const obj of descriptor.objects) {
    if (obj.id === AVATAR_ID) {
      throw new Error(`オブジェクト id に予約語 ${AVATAR_ID} は使えません`)
    }
    if (ids.has(obj.id)) {
      throw new Error(`オブジェクト id が重複しています: ${obj.id}`)
    }
    if (names.has(obj.name)) {
      throw new Error(`オブジェクト name が重複しています: ${obj.name}`)
    }
    if (!isFiniteVec3(obj.position) || !isFiniteVec3(obj.size)) {
      throw new Error(`座標/サイズが不正です: ${obj.id}`)
    }
    if (!isPositiveVec3(obj.size)) {
      throw new Error(`サイズは正の値が必要です: ${obj.id}`)
    }
    ids.add(obj.id)
    names.add(obj.name)
  }
  const avatar = descriptor.avatar
  if (avatar && (!isFiniteVec3(avatar.spawn) || !isPositiveVec3(avatar.size))) {
    throw new Error('アバターの spawn/size が不正です')
  }
}

/**
 * シーンの見た目・空間状態を保持する World の標準実装。
 *
 * 設計方針:
 * - コンテナ（Map）のインスタンスは固定し、各オブジェクトの状態値は
 *   変更のたびに「新しいオブジェクト」に差し替える（値は不変スナップショット）。
 *   毎フレーム Map 全体を作り直すコストを避けつつ、購読側は値の同一性比較で
 *   変化を検知できる。
 * - メッセージ/イベント配送はここでは扱わない（Runtime の責務）。
 */
export class SceneWorld implements EditablePreviewWorld {
  private readonly states = new Map<string, ObjectRuntimeState>()
  private readonly listeners = new Set<WorldChangeListener>()
  // アバター歩行速度 (stud/秒)。descriptor.avatar.speed を初期値とし、
  // events_setWalkSpeedTo で書き換え可能。reset() で descriptor 値に戻る。
  private avatarWalkSpeed = 0
  // アバターのジャンプ力（Roblox JumpPower 既定 50）と体力（MaxHealth 既定 100）。
  // events_setJumpPowerTo / setHealthTo で書き換え、reset() で既定へ戻る。
  private avatarJumpPower = DEFAULT_JUMP_POWER
  private avatarHealth = DEFAULT_MAX_HEALTH
  /** 未処理のアバターテレポート要求（ワープギミック）。物理側が次ステップで消費する。 */
  private pendingAvatarTeleport: Vec3 | null = null
  private readonly pendingVelocityRequests = new Map<
    string,
    ObjectVelocityRequest
  >()

  constructor(private readonly descriptor: SceneDescriptor) {
    validateSceneDescriptor(descriptor)
    this.reset()
  }

  getState(): ReadonlyMap<string, ObjectRuntimeState> {
    return this.states
  }

  subscribe(listener: WorldChangeListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  reset(): void {
    this.pendingVelocityRequests.clear()
    this.states.clear()
    for (const obj of this.descriptor.objects) {
      this.states.set(
        obj.id,
        initialState({
          id: obj.id,
          name: obj.name,
          position: obj.position,
          color: obj.color,
          size: obj.size,
          material: obj.material ?? DEFAULT_MATERIAL,
          physicalProperties: obj.physicalProperties,
          transparency: obj.transparency,
          // 基準向きが rbxl 由来で与えられている場合、実行時ヨーは 0 始まり
          // （rbxl の向きは orientation 側に含まれるため二重適用しない）。
          // orientation 不在の旧シーンは rotationY をそのまま実行時ヨーに使う。
          rotationY: obj.orientation ? 0 : obj.rotationY,
          orientation: obj.orientation,
          canCollide: obj.canCollide,
          canTouch: obj.canTouch,
          isStatic: obj.isStatic,
          dynamicOnUnanchor: obj.dynamicOnUnanchor,
          shape: obj.shape,
          fire: obj.fire,
          sparkle: obj.sparkle,
          soundVolume: obj.sound?.volume,
        }),
      )
    }
    const avatar = this.descriptor.avatar
    if (avatar) {
      this.states.set(
        AVATAR_ID,
        initialState({
          id: AVATAR_ID,
          name: 'Avatar',
          position: avatar.spawn,
          // 実機同様 SpawnLocation の向きでスポーンする（例: RJF03 は 180 度）。
          rotationY: avatar.spawnYaw,
          color: avatar.color ?? DEFAULT_AVATAR_COLOR,
          size: avatar.size,
          material: DEFAULT_MATERIAL,
        }),
      )
    }
    // アバター速度を descriptor 既定へ戻す（events_setWalkSpeedTo の上書きを無効化）。
    // 負値・非数を弾く（setAvatarWalkSpeed と同じ防御）。
    this.avatarWalkSpeed = Math.max(0, avatar?.speed ?? 0)
    // ジャンプ力・体力も既定へ戻す（events_setJumpPowerTo / setHealthTo を無効化）。
    this.avatarJumpPower = DEFAULT_JUMP_POWER
    this.avatarHealth = DEFAULT_MAX_HEALTH
    // 未消費のテレポート要求は前プレイの残骸なので破棄する。
    this.pendingAvatarTeleport = null
    this.notify()
  }

  setAvatarWalkSpeed(speed: number): void {
    assertFinite(speed, 'setAvatarWalkSpeed.speed')
    // 負値は受け付けない（Roblox の Humanoid.WalkSpeed と同様にクランプ）。
    this.avatarWalkSpeed = Math.max(0, speed)
  }

  getAvatarWalkSpeed(): number {
    return this.avatarWalkSpeed
  }

  setAvatarJumpPower(power: number): void {
    assertFinite(power, 'setAvatarJumpPower.power')
    // 負値は受け付けない（Roblox の Humanoid.JumpPower と同様）。
    this.avatarJumpPower = Math.max(0, power)
  }

  getAvatarJumpPower(): number {
    return this.avatarJumpPower
  }

  setAvatarHealth(health: number): void {
    assertFinite(health, 'setAvatarHealth.health')
    this.avatarHealth = health
  }

  getAvatarHealth(): number {
    return this.avatarHealth
  }

  resetAvatarStats(): void {
    // 実機の死亡リスポーンは Character/Humanoid の再生成。WalkSpeed/JumpPower/
    // Health はプログラムの上書きが失効して既定へ戻る（reset() と同じ既定値）。
    this.avatarWalkSpeed = Math.max(0, this.descriptor.avatar?.speed ?? 0)
    this.avatarJumpPower = DEFAULT_JUMP_POWER
    this.avatarHealth = DEFAULT_MAX_HEALTH
  }

  requestAvatarTeleport(position: Vec3): void {
    assertFinite(position.x, 'requestAvatarTeleport.position.x')
    assertFinite(position.y, 'requestAvatarTeleport.position.y')
    assertFinite(position.z, 'requestAvatarTeleport.position.z')
    this.pendingAvatarTeleport = { ...position }
  }

  consumeAvatarTeleport(): Vec3 | null {
    const pending = this.pendingAvatarTeleport
    this.pendingAvatarTeleport = null
    return pending
  }

  moveBy(objectId: string, delta: Vec3): void {
    assertFiniteVec3(delta, 'moveBy.delta')
    this.update(objectId, (s) => ({
      ...s,
      position: {
        x: s.position.x + delta.x,
        y: s.position.y + delta.y,
        z: s.position.z + delta.z,
      },
    }))
  }

  setPosition(objectId: string, position: Vec3): void {
    assertFiniteVec3(position, 'setPosition.position')
    this.update(objectId, (s) => ({ ...s, position: { ...position } }))
  }

  rotateYBy(objectId: string, radians: number): void {
    assertFinite(radians, 'rotateYBy.radians')
    this.update(objectId, (s) => ({ ...s, rotationY: s.rotationY + radians }))
  }

  setRotationY(objectId: string, radians: number): void {
    assertFinite(radians, 'setRotationY.radians')
    this.update(objectId, (s) => ({ ...s, rotationY: radians }))
  }

  setAvatarPose(position: Vec3, yaw: number): void {
    assertFiniteVec3(position, 'setAvatarPose.position')
    assertFinite(yaw, 'setAvatarPose.yaw')
    this.update(AVATAR_ID, (s) => ({
      ...s,
      position: { ...position },
      rotationY: yaw,
    }))
  }

  setOrientation(objectId: string, orientation: Quat): void {
    assertFinite(orientation.x, 'setOrientation.x')
    assertFinite(orientation.y, 'setOrientation.y')
    assertFinite(orientation.z, 'setOrientation.z')
    assertFinite(orientation.w, 'setOrientation.w')
    // 完全姿勢を orientation に格納し rotationY=0（描画/物理は composeYaw で合成）。
    this.update(objectId, (s) => ({
      ...s,
      orientation: {
        x: orientation.x,
        y: orientation.y,
        z: orientation.z,
        w: orientation.w,
      },
      rotationY: 0,
    }))
  }

  setTransparency(objectId: string, transparency: number): void {
    assertFinite(transparency, 'setTransparency.transparency')
    this.update(objectId, (s) => ({
      ...s,
      transparency: clamp(transparency, 0, 1),
    }))
  }

  setVisible(objectId: string, visible: boolean): void {
    this.update(objectId, (s) => ({ ...s, visible }))
  }

  setCanCollide(objectId: string, canCollide: boolean): void {
    this.update(objectId, (s) => ({ ...s, canCollide }))
  }

  setCanTouch(objectId: string, canTouch: boolean): void {
    this.update(objectId, (s) => ({ ...s, canTouch }))
  }

  setClickable(objectId: string, clickable: boolean): void {
    this.update(objectId, (s) => ({ ...s, clickable }))
  }

  setSoundVolume(objectId: string, volume: number): void {
    assertFinite(volume, 'setSoundVolume.volume')
    this.update(objectId, (s) => {
      // Sound を持たないオブジェクトには効かない（undefined を保つ）。
      if (s.soundVolume === undefined) {
        return s
      }
      // Roblox Sound.Volume の範囲は 0〜10。
      return { ...s, soundVolume: clamp(volume, 0, 10) }
    })
  }

  setAnchored(objectId: string, anchored: boolean): void {
    this.update(objectId, (s) => ({ ...s, anchored }))
  }

  setObjectVelocity(objectId: string, velocity: Vec3): void {
    assertFiniteVec3(velocity, 'setObjectVelocity.velocity')
    if (!this.states.has(objectId)) return
    this.pendingVelocityRequests.set(objectId, {
      ...this.pendingVelocityRequests.get(objectId),
      linear: { ...velocity },
    })
    this.update(objectId, (s) => ({ ...s, velocity: { ...velocity } }))
  }

  setObjectAngularVelocity(objectId: string, angularVelocity: Vec3): void {
    assertFiniteVec3(
      angularVelocity,
      'setObjectAngularVelocity.angularVelocity',
    )
    if (!this.states.has(objectId)) return
    this.pendingVelocityRequests.set(objectId, {
      ...this.pendingVelocityRequests.get(objectId),
      angular: { ...angularVelocity },
    })
    this.update(objectId, (s) => ({
      ...s,
      angularVelocity: { ...angularVelocity },
    }))
  }

  consumeObjectVelocityRequest(objectId: string): ObjectVelocityRequest | null {
    const request = this.pendingVelocityRequests.get(objectId) ?? null
    this.pendingVelocityRequests.delete(objectId)
    return request
  }

  setDynamicTransforms(
    updates: ReadonlyArray<{
      objectId: string
      position: Vec3
      orientation: Quat
      velocity?: Vec3
      angularVelocity?: Vec3
    }>,
  ): void {
    if (updates.length === 0) {
      return
    }
    // 完全姿勢を orientation に載せ、実行時ヨーは 0 にする（描画は
    // Qy(0)∘orientation = orientation を使う）。動的剛体の任意回転を表せる。
    // 全件を更新してから 1 度だけ notify し、毎ステップの再描画を 1 回に抑える。
    for (const {
      objectId,
      position,
      orientation,
      velocity,
      angularVelocity,
    } of updates) {
      assertFiniteVec3(position, 'setDynamicTransforms.position')
      const current = this.states.get(objectId)
      if (!current) {
        continue
      }
      this.states.set(objectId, {
        ...current,
        position: { ...position },
        orientation: { ...orientation },
        rotationY: 0,
        velocity: velocity ? { ...velocity } : current.velocity,
        angularVelocity:
          angularVelocity ? { ...angularVelocity } : current.angularVelocity,
      })
    }
    this.notify()
  }

  addObject(spec: NewObjectSpec): void {
    if (spec.id === AVATAR_ID) {
      throw new Error(`オブジェクト id に予約語 ${AVATAR_ID} は使えません`)
    }
    if (this.states.has(spec.id)) {
      throw new Error(`既に存在する id です: ${spec.id}`)
    }
    this.states.set(
      spec.id,
      initialState({
        id: spec.id,
        name: spec.name,
        position: spec.position,
        color: spec.color,
        size: normalizeSize(spec.size),
        material: spec.material ?? DEFAULT_MATERIAL,
        physicalProperties: spec.physicalProperties,
        // control_clone は複製元の姿勢・透明度・衝突/固定を引き継ぐ（省略時は既定）。
        rotationY: spec.rotationY,
        orientation: spec.orientation,
        transparency: spec.transparency,
        canCollide: spec.canCollide,
        canTouch: spec.canTouch,
        isStatic: spec.isStatic,
        shape: spec.shape,
        fire: spec.fire,
        sparkle: spec.sparkle,
        initialVelocity: spec.initialVelocity,
        collisionScale: spec.collisionScale,
      }),
    )
    this.notify()
  }

  setSize(objectId: string, size: Vec3): void {
    this.update(objectId, (s) => ({ ...s, size: normalizeSize(size) }))
  }

  setColor(objectId: string, color: string): void {
    this.update(objectId, (s) => ({ ...s, color }))
  }

  setMaterial(objectId: string, material: RobloxMaterial): void {
    this.update(objectId, (s) => ({ ...s, material }))
  }

  exists(objectId: string): boolean {
    return this.states.has(objectId)
  }

  destroy(objectId: string): void {
    this.pendingVelocityRequests.delete(objectId)
    if (this.states.delete(objectId)) {
      this.notify()
    }
  }

  private update(
    objectId: string,
    fn: (state: ObjectRuntimeState) => ObjectRuntimeState,
  ): void {
    const current = this.states.get(objectId)
    if (!current) {
      return
    }
    this.states.set(objectId, fn(current))
    this.notify()
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.states)
    }
  }
}
